import React, { useState } from "react";
import Can from "../../components/can";
import { Redirect } from "react-router-dom";
import s from "./index.module.css";
import * as classnames from "classnames";
import { alertAdd } from "../../redux/actions";
import { brand } from "../../theme";
import { useDispatch, useSelector } from "react-redux";
import { changeMyPassword } from "../../redux/person/actions";
import { Box, IconButton, InputAdornment } from "@material-ui/core";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import { getLoginRedirectPath } from "../../redux/common";
import { usePasswordStrength } from "../../hooks/usePasswordStrength";
import { PasswordStrengthMeter } from "../../components/password-strength-meter";
import { CssTextField } from "../../components/css-text-field";

const PasswordChangePage = ({ history }) => {
  const [state, setState] = useState({
    newPassword: "",
    oldPassword: "",
    confirmedNewPassword: "",
  });
  const [busy, setBusy] = useState(false);
  const {
    score: passwordScore,
    requirements: passwordRequirements,
  } = usePasswordStrength(state.newPassword, { maxLength: null });
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmedNewPassword, setShowConfirmedNewPassword] = useState(false);
  const dispatch = useDispatch();
  const globalTheme = useSelector((state) => state.globalTheme);
  const isHighContrast = globalTheme === "high-contrast";
  const hcLabel = isHighContrast ? { color: "yellow" } : undefined;
  const hcButton = (base) =>
    isHighContrast
      ? { backgroundColor: "black", color: "yellow", border: "1px solid yellow" }
      : base;

  const visibilityToggle = (visible, setVisible) => ({
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          onClick={() => setVisible(!visible)}
          aria-label="toggle password visibility"
          size="small"
        >
          {visible ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    ),
  });

  const handleChange = (event) => {
    const { name, value } = event.target;
    setState((prevState) => ({ ...prevState, [name]: value }));
  };

  const isNewPasswordStrong = (pwd) =>
    pwd.length > 7 &&
    /[a-z]/.test(pwd) &&
    /[A-Z]/.test(pwd) &&
    /[0-9]/.test(pwd) &&
    /[!@#$%^&+=]+/.test(pwd);

  const changePassword = async () => {
    setBusy(true);
    const isPasswordChanged = await dispatch(
      changeMyPassword({
        oldPassword: state.oldPassword,
        newPassword: state.newPassword,
      })
    );
    if (isPasswordChanged) {
      dispatch(
        alertAdd({
          text: "Hasło zostało zmienione.",
          isSuccess: true,
        })
      );

      setState({
        newPassword: "",
        oldPassword: "",
        confirmedNewPassword: "",
      });
    } else {
      dispatch(
        alertAdd({
          text: "Wprowadzone aktualne hasło jest niepoprawne.",
          isError: true,
        })
      );
    }
    setBusy(false);
  };

  const handleCancelClick = (event) => {
    history.push("/");
  };

  const handleSaveClick = (e) => {
    e.preventDefault();

    if (state.oldPassword === "") {
      dispatch(
        alertAdd({ text: "Wprowadź aktualne hasło.", isError: true })
      );
      return;
    }

    if (
      state.newPassword === "" ||
      state.newPassword !== state.confirmedNewPassword
    ) {
      dispatch(
        alertAdd({
          text:
            "Nie wprowadzono nowego hasła lub wprowadzone nowe hasło nie zgadza się z powtórzonym hasłem.",
          isError: true,
        })
      );
      return;
    }

    if (!isNewPasswordStrong(state.newPassword)) {
      dispatch(
        alertAdd({
          text:
            "Nowe hasło nie spełnia polityki silnego hasła: minimum 8 znaków, wielka i mała litera, cyfra oraz znak specjalny z zestawu ! @ # $ % ^ & + =.",
          isError: true,
        })
      );
      return;
    }

    changePassword();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSaveClick(e);
    }
  };

  const page = () => (
    <div className="container is-fluid">
      <p className="title" style={hcLabel}>Zmiana hasła</p>

      <form
        id="change_password_form"
        onSubmit={handleSaveClick}
        noValidate
        autoComplete="off"
      ></form>

      <div className="columns">
        <div className={classnames("column is-half")}>
          <table
            className={`table is-fullwidth ${s.noborder}`}
            style={isHighContrast ? { backgroundColor: "transparent" } : undefined}
          >
            <tbody>
              <tr>
                <td style={hcLabel}>Aktualne hasło</td>
                <td>
                  <CssTextField
                    variant="outlined"
                    size="small"
                    type={showOldPassword ? "text" : "password"}
                    name="oldPassword"
                    fullWidth
                    value={state.oldPassword}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    InputProps={visibilityToggle(showOldPassword, setShowOldPassword)}
                  />
                </td>
              </tr>
              <tr>
                <td style={hcLabel}>Nowe hasło</td>
                <td>
                  <CssTextField
                    variant="outlined"
                    size="small"
                    type={showNewPassword ? "text" : "password"}
                    name="newPassword"
                    fullWidth
                    onKeyDown={handleKeyDown}
                    value={state.newPassword}
                    onChange={handleChange}
                    InputProps={visibilityToggle(showNewPassword, setShowNewPassword)}
                  />
                </td>
              </tr>
              <tr>
                <td style={hcLabel}>Powtórz nowe hasło</td>
                <td>
                  <CssTextField
                    variant="outlined"
                    size="small"
                    type={showConfirmedNewPassword ? "text" : "password"}
                    name="confirmedNewPassword"
                    fullWidth
                    onKeyDown={handleKeyDown}
                    value={state.confirmedNewPassword}
                    onChange={handleChange}
                    InputProps={visibilityToggle(
                      showConfirmedNewPassword,
                      setShowConfirmedNewPassword
                    )}
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <PasswordStrengthMeter
            score={passwordScore}
            requirements={passwordRequirements}
            highContrast={isHighContrast}
          />
        </div>
      </div>

      <div className="field is-grouped is-grouped-centered">
        <div className="control">
          <button
            className="button"
            onClick={handleCancelClick}
            name="clear"
            style={hcButton(undefined)}
          >
            Anuluj
          </button>
        </div>
        <div className="control">
          <button
            form="change_password_form"
            type="submit"
            className={classnames("button is-primary", { "is-loading": busy })}
            style={hcButton({ backgroundColor: brand.teal, color: "white" })}
            name="save"
          >
            Zapisz
          </button>
        </div>
      </div>
    </div>
  );

  const redirect = () => <Redirect to={getLoginRedirectPath()} />;

  return <Can permission="passwordChangePage:view" ok={page} not={redirect} />;
};

export default PasswordChangePage;
